import { FC, useCallback, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";

import { AppleMap } from "capitalrx-components-2.0";
import { LoadingGroup, LoadingSkeleton, useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Paragraph, Subtitle } from "capitalrx-components-2.0/lib/typography";

import { useGetPharmacyQuery } from "@/App/api/pharmacy";
import { ContentPanel, PageWrapper, PharmacyDetails, ThemedButton } from "@/App/components";
import { useNavigateClient } from "@/App/hooks";

export const NearbyPharmacyDetailPage: FC = () => {
  const { t: translate } = useTranslation();
  const { pharmacy_id: providerId } = useParams();
  const alert = useAlert();
  const { data: pharmacy, isLoading: isLoadingPharmacy, isError } = useGetPharmacyQuery(providerId, !!providerId);
  const navigate = useNavigateClient();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (isError) {
      alert({
        message: translate("NEARBY_PHARMACY.nearbyPharmacyDetailPage.unableToFindPharmacy"),
        type: "error",
        style: "toast",
      });
      navigate("/nearby-pharmacy");
    }
  }, [isError]);

  const onBackPressed = useCallback(() => {
    navigate({
      pathname: "/nearby-pharmacy/compare-pharmacies",
      search: searchParams.toString(),
    });
  }, []);

  const { isCompressed } = useResponsive({
    isCompressed: {
      desktop: false,
      mobile: true,
    },
  });

  const latitude = pharmacy?.latitude;
  const longitude = pharmacy?.longitude;

  const pharmacyDescription = useMemo(() => {
    const overrideDescription = pharmacy?.pharmacy_display_rules?.description;
    if (overrideDescription) {
      return overrideDescription;
    } else {
      if (pharmacy?.distance) {
        return `${Math.round(pharmacy.distance * 100) / 100} miles | ${pharmacy?.address_line_1 || ""} ${pharmacy.address_line_2 || ""}`;
      } else {
        return `${pharmacy?.address_line_1 || ""} ${pharmacy?.address_line_2 || ""}`;
      }
    }
  }, [latitude, longitude, pharmacy]);

  return (
    <PageWrapper>
      <LoadingGroup loading={isLoadingPharmacy}>
        <ContentPanel
          title={
            <Stack spacing="small">
              <Shelf>
                <ThemedButton testId="go-back" onClick={onBackPressed} appearance="secondary" icon="arrow-left">
                  {translate("NEARBY_PHARMACY.nearbyPharmacyDetailPage.back")}
                </ThemedButton>
                <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 200, height: 46 }} />}>
                  <Subtitle testId="pharmacy-name">{pharmacy?.name}</Subtitle>
                </LoadingGroup.Placeholder>
                <Shelf.Spacer />
              </Shelf>
              <Paragraph testId="pharmacy-description">{pharmacyDescription}</Paragraph>
            </Stack>
          }
        >
          <Grid>
            <Grid.Span columns={isCompressed ? 12 : 6}>
              {!!pharmacy && <PharmacyDetails {...{ ...pharmacy, isLoading: isLoadingPharmacy }} />}
            </Grid.Span>
            <Grid.Span columns={isCompressed ? 12 : 6}>
              {!!pharmacy && (
                <AppleMap
                  style={{ height: isCompressed ? "400px" : "100%" }}
                  locations={[{ latitude: parseFloat(pharmacy.latitude), longitude: parseFloat(pharmacy.longitude) }]}
                />
              )}
            </Grid.Span>
          </Grid>
        </ContentPanel>
      </LoadingGroup>
    </PageWrapper>
  );
};
